import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

// material-ui
import {
    Grid,
    Alert,
    Box,
    Stack,
    AvatarGroup,
    Avatar,
    IconButton,
    Typography,
    Tooltip,
    Skeleton,
    Button,
    Snackbar,
    Dialog,
    DialogTitle,
    Divider,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    LinearProgress
} from '@mui/material';

import { ethers } from 'ethers';
import { useParams } from 'react-router';

// Firestore Import
import db from '../../firebase';

// project imports
import CustomButton from 'ui-component/tiles/CustomButton';
import Header from '../../ui-component/tiles/Header';
import { gridSpacing } from 'store/constant';
import { IconActivity, IconBuildingStore, IconCrown, IconExternalLink, IconReceipt, IconReportMoney, IconTicket } from '@tabler/icons';
import InfoIcon from '@mui/icons-material/Info';
import Countdown from 'react-countdown';
import CustomListItem from 'ui-component/CustomListItem';
import HeadingSection from 'ui-component/HeadingSection';
import { makeStyles } from '@material-ui/core/styles';

// Contract Imports
import { nftContractAbi } from 'utils/contractAbis/nftContractAbi';
import polygonContractData from 'utils/contractAddresses/polygonContractData';
import axios from 'axios';

// ==============================|| DEFAULT Token Presales ||============================== //

const NFTDrops = ({ wallet, isAdmin, setIsShowing, chainInformation }) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(false);
    const [snackbarType, setSnackbarType] = useState('success');

    const [presaleContract, setPresaleContract] = useState(null);
    const [presaleDialogOpened, setPresaleDialogOpened] = useState(false);
    const [presaleContributionValue, setPresaleContributionValue] = useState('1');
    // ------------------------------------------------------------------------
    const [presaleHardCap, setPresaleHardCap] = useState(null);
    const [presaleStartTime, setPresaleStartTime] = useState(null);
    const [presaleWhitelistStartTime, setPresaleWhitelistStartTime] = useState(null);
    const [mintCost, setMintCost] = useState(null);
    const [whitelistMintCost, setWhitelistMintCost] = useState(null);
    // ------------------------------------------------------------------------
    const [presaleUserWhitelistStatus, setPresaleUserWhitelistStatus] = useState(false);
    const [presaleTotalAllocations, setPresaleTotalAllocations] = useState(null);
    // ------------------------------------------------------------------------
    const [databaseDropsData, setDatabaseDropsData] = useState(null);
    const [selectedDropIndex, setSelectedDropIndex] = useState(-1);
    //  ------------------------------------------------------------------------
    const [refresher, setRefresher] = useState(false);
    const [processing, setProcessing] = useState(false);

    const useStyles = makeStyles({
        input: {
            color: '#ffffff'
        }
    });

    const classes = useStyles();

    const handleClose = () => {
        setSnackbarOpen(false);
    };

    const handlePresaleDialogClosed = () => {
        setPresaleDialogOpened(false);
    };

    const mint = () => {
        const currentTime = Math.floor(Date.now() / 1000);
        const userStartTimestamp = presaleUserWhitelistStatus == true ? presaleWhitelistStartTime : presaleStartTime;

        if (!processing) {
            if (currentTime >= userStartTimestamp) {
                if (
                    presaleContributionValue !== undefined &&
                    presaleContributionValue !== null &&
                    presaleContributionValue > 0 &&
                    presaleContributionValue <= 10
                ) {
                    setProcessing(true);
                    wallet.getAddress().then((_address) => {
                        presaleContract.mintCost(_address).then((_cost) => {
                            const totalCost = _cost.mul(parseInt(presaleContributionValue.toString()));
                            presaleContract
                                .mint(parseInt(presaleContributionValue), { value: totalCost.toString() })
                                .then((_txn) => {
                                    wallet.provider.waitForTransaction(_txn.hash).then((_tx) => {
                                        setSnackbarMessage(`Successfully Minted!`);
                                        setSnackbarType('success');
                                        setSnackbarOpen(true);
                                        setProcessing(false);
                                    });
                                })
                                .catch((_err) => {
                                    setSnackbarMessage(`Something went wrong! Please try again.`);
                                    setSnackbarType('error');
                                    setSnackbarOpen(true);
                                    setProcessing(false);
                                });
                        });
                    });
                } else {
                    setSnackbarMessage(`Please enter an amount greater than 1 and less than 10.`);
                    setSnackbarType('error');
                    setSnackbarOpen(true);
                }
            } else {
                setSnackbarMessage(`Mint has not begun yet.`);
                setSnackbarType('error');
                setSnackbarOpen(true);
            }
        }
    };

    const initContracts = (selectedContract) => {
        if (wallet !== null && polygonContractData.presaleContractAddress !== null) {
            console.log(selectedContract !== null ? selectedContract : polygonContractData.presaleContractAddress);
            setPresaleContract(new ethers.Contract(selectedContract, nftContractAbi, wallet));
        }
    };

    const setPresaleInformation = () => {
        if (presaleContract !== null && wallet !== null) {
            presaleContract.mintStartTimestamp().then((_result) => {
                // console.log(_result.toString());
                setPresaleStartTime(_result.toString());
            });

            presaleContract.whitelistMintStartTimestamp().then((_result) => {
                // console.log(_result.toString());
                setPresaleWhitelistStartTime(_result.toString());
            });

            presaleContract.totalSupply().then((_supply) => {
                setPresaleTotalAllocations(_supply.toString());
            });

            presaleContract.maxSupply().then((_supply) => {
                setPresaleHardCap(_supply.toString());
            });

            presaleContract.normalMintCost().then((_cost) => {
                setMintCost(_cost.toString());
            });

            presaleContract.whitelistMintCost().then((_cost) => {
                setWhitelistMintCost(_cost.toString());
            });

            wallet.getAddress().then((_address) => {
                presaleContract.whitelist(_address).then((_status) => {
                    // console.log(_status);
                    setPresaleUserWhitelistStatus(_status);
                });
            });
        }
    };

    const statusText = (_status) => {
        switch (_status) {
            case 0:
                return {
                    text: 'In Progress',
                    color: '#ffa500'
                };
            case 1:
                return {
                    text: 'Successful',
                    color: '#00ff00'
                };
            default:
                return {
                    text: 'Unsuccessful',
                    color: '#F47174'
                };
        }
    };

    const handleViewDrop = (_index) => {
        if (chainInformation !== null) {
            const _drop = databaseDropsData[_index];
            if (_drop !== undefined) {
                if (chainInformation.chainName !== _drop.chain) {
                    setSnackbarMessage(`Please connect on the ${_drop.chainName} Chain to view this drop.`);
                    setSnackbarType('error');
                    setSnackbarOpen(true);

                    try {
                        window.ethereum.request({
                            method: 'wallet_switchEthereumChain',
                            params: [{ chainId: ethers.utils.hexlify(_drop.chainId) }]
                        });
                    } catch (err) {
                        // This error code indicates that the chain has not been added to MetaMask
                        if (err.code === 4902) {
                            window.ethereum.request({
                                method: 'wallet_addEthereumChain',
                                params: [
                                    {
                                        chainName: _drop.chainName,
                                        chainId: ethers.utils.hexlify(_drop.chainId),
                                        nativeCurrency: { name: _drop.coinSymbol, decimals: 18, symbol: _drop.coinSymbol },
                                        rpcUrls: [_drop.chainRpc]
                                    }
                                ]
                            });
                        }
                    }
                } else {
                    setSelectedDropIndex(_index);
                    setTimeout(function () {
                        setPresaleDialogOpened(true);
                    }, 200);
                }
            }
        }
    };

    const fetchFirestoreDrops = () => {
        /*db.collection('nft-drops')
            .orderBy('startTimestamp', 'desc')
            .where('chain', '==', chainInformation.chainName)
            .get()
            .then((querySnapshot) => {
                const tempDrops = [];
                querySnapshot.forEach((doc) => {
                    tempDrops.push(doc.data());
                });
                setDatabaseDropsData(tempDrops);
            });*/

        axios.get('https://us-central1-phenix-app-881db.cloudfunctions.net/getNFTDrops').then((_result) => {
            const data = _result.data;
            if (data !== undefined) {
                for (var i = 0; i < data.length; i++) {
                    if (data[i].chain !== undefined) {
                        switch (data[i].chain) {
                            case 'cronos':
                                data[i].coinSymbol = 'CRO';
                                data[i].chainName = 'Cronos';
                                data[i].chainId = 25;
                                data[i].chainRpc = 'https://evm-cronos.crypto.org/';

                                break;
                            case 'polygon':
                                data[i].coinSymbol = 'MATIC';
                                data[i].chainName = 'Polygon';
                                data[i].chainId = 137;
                                data[i].chainRpc = 'https://polygon-rpc.com/';
                                break;
                            default:
                                data[i].coinSymbol = 'CRO';
                                data[i].chainName = 'Cronos';
                                data[i].chainId = 25;
                                data[i].chainRpc = 'https://evm-cronos.crypto.org/';
                        }
                    } else {
                        data[i].coinSymbol = 'CRO';
                        data[i].chainName = 'Cronos';
                        data[i].chainId = 25;
                        data[i].chainRpc = 'https://evm-cronos.crypto.org/';
                    }
                }
                setDatabaseDropsData(data);
            }
        });
    };

    useEffect(() => {
        setTimeout(function () {
            setRefresher(!refresher);
        }, 5000);

        setPresaleInformation();
    }, [refresher]);

    useEffect(() => {
        setPresaleInformation();
    }, [presaleContract]);

    useEffect(() => {
        if (chainInformation !== null && databaseDropsData == null) {
            fetchFirestoreDrops();
        }
        /*if (chainInformation.chainId != 137) {
            try {
                window.ethereum.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: ethers.utils.hexlify(137) }]
                });
            } catch (err) {
                // This error code indicates that the chain has not been added to MetaMask
                if (err.code === 4902) {
                    window.ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [
                            {
                                chainName: 'Polygon',
                                chainId: ethers.utils.hexlify(137),
                                nativeCurrency: { name: 'MATIC', decimals: 18, symbol: 'MATIC' },
                                rpcUrls: ['https://polygon-rpc.com/']
                            }
                        ]
                    });
                }
            }
        }*/
    }, [chainInformation]);

    useEffect(() => {
        if (selectedDropIndex !== -1 && selectedDropIndex !== null) {
            initContracts(databaseDropsData[selectedDropIndex].contractAddress);
        }
    }, [selectedDropIndex]);

    useEffect(() => {
        // initContracts();

        // TODO:
        // > Setup Firebase Colleciton with different presale data
        // > Update Template below to loop different presales.
        // > Add check for presaleName to find existing presale and open associated popup

        if (chainInformation !== null && databaseDropsData == null) {
            fetchFirestoreDrops();
        }
    }, []);

    return (
        <>
            <Helmet>
                <title>NFT Drops | Phenix Finance</title>
            </Helmet>
            <Snackbar open={snackbarOpen} autoHideDuration={2000} onClose={handleClose}>
                <Alert onClose={handleClose} severity={snackbarType} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Dialog fullWidth maxWidth="xs" onClose={handlePresaleDialogClosed} open={presaleDialogOpened}>
                <DialogTitle>Enter {selectedDropIndex !== -1 ? databaseDropsData[selectedDropIndex].name : ''} Drop</DialogTitle>
                <Stack spacing={1} pb={2} pl={2} pr={2}>
                    <Box sx={{ position: 'relative' }}>
                        <Box
                            sx={{
                                width: '100%',
                                paddingTop: '100%',
                                borderRadius: '8px',
                                backgroundColor: '#181a1d',
                                position: 'relative'
                            }}
                        >
                            <img
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    width: '75px',
                                    transform: 'translate(-50%, -50%)',
                                    opacity: 0.5
                                }}
                                src="resources/images/loader.svg"
                                alt="loading"
                            />
                        </Box>
                        <img
                            src={selectedDropIndex !== -1 ? databaseDropsData[selectedDropIndex].image : ''}
                            style={{
                                width: '100%',
                                border: '2px solid rgba(114,205,212, 0.2)',
                                borderRadius: 8,
                                boxShadow: 'rgba(114,205,212, 0.1) 0px 7px 29px 0px',
                                position: 'absolute',
                                top: 0,
                                left: 0
                            }}
                            alt={selectedDropIndex !== -1 ? databaseDropsData[selectedDropIndex].name : ''}
                        />
                        <img
                            src={`resources/images/drop_borders/${
                                selectedDropIndex !== -1 ? databaseDropsData[selectedDropIndex].chain : 'cronos'
                            }_border.png`}
                            style={{
                                width: '100%',
                                border: '2px solid rgba(114,205,212, 0.2)',
                                borderRadius: 8,
                                boxShadow: 'rgba(114,205,212, 0.1) 0px 7px 29px 0px',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                zIndex: 2
                            }}
                            alt={selectedDropIndex !== -1 ? databaseDropsData[selectedDropIndex].name : ''}
                        />
                    </Box>

                    <HeadingSection heading="Introduction" icon={<InfoIcon />} />

                    <Stack pl={0.5}>
                        {selectedDropIndex !== -1 && databaseDropsData[selectedDropIndex].description !== undefined ? (
                            <Typography variant="p">{databaseDropsData[selectedDropIndex].description}</Typography>
                        ) : (
                            ''
                        )}

                        {selectedDropIndex !== -1 && databaseDropsData[selectedDropIndex].listDescription !== undefined
                            ? databaseDropsData[selectedDropIndex].listDescription.map((_listItem) => (
                                  <CustomListItem
                                      key={_listItem.title + _listItem.info}
                                      heading={_listItem.title}
                                      text={''}
                                      info={_listItem.info}
                                  />
                              ))
                            : ''}
                    </Stack>

                    <HeadingSection heading="Presale Details" icon={<InfoIcon />} />

                    <Stack pl={0.5}>
                        <CustomListItem
                            heading={
                                presaleStartTime !== null &&
                                presaleWhitelistStartTime !== null &&
                                presaleTotalAllocations !== null &&
                                presaleHardCap !== null ? (
                                    'Starts In'
                                ) : (
                                    <Skeleton width={50} />
                                )
                            }
                            text={
                                presaleStartTime !== null && presaleWhitelistStartTime !== null ? (
                                    Math.floor(Date.now() / 1000) <
                                    (presaleUserWhitelistStatus == true ? presaleWhitelistStartTime : presaleStartTime) ? (
                                        <Countdown
                                            date={
                                                presaleUserWhitelistStatus == true
                                                    ? presaleWhitelistStartTime * 1000
                                                    : presaleStartTime * 1000
                                            }
                                        />
                                    ) : (
                                        <Typography variant="p">
                                            {parseInt(presaleTotalAllocations) == parseInt(presaleHardCap) ? 'Completed' : 'In Progress'}
                                        </Typography>
                                    )
                                ) : (
                                    <Skeleton width={50} />
                                )
                            }
                            info="Time until the presale is accessible."
                        />
                        <Box sx={{ width: '100%', height: '1px', background: '#ccc', margin: '0.5rem 0' }} />
                        <CustomListItem
                            heading="Total Supply:"
                            text={presaleHardCap !== null ? ethers.utils.commify(presaleHardCap) : <Skeleton width={50} />}
                            info="The maximum allocation that can be reach."
                        />

                        <Box sx={{ width: '100%', height: '1px', background: '#ccc', margin: '0.5rem 0' }} />
                        <CustomListItem
                            heading="Mint Cost:"
                            text={
                                mintCost !== null ? (
                                    ethers.utils.commify(ethers.utils.formatEther(mintCost)) + ` ${chainInformation.coinSymbol}`
                                ) : (
                                    <Skeleton width={50} />
                                )
                            }
                            info="The maximum allocation that can be reach."
                        />
                        <CustomListItem
                            heading="WL Cost:"
                            text={
                                whitelistMintCost !== null ? (
                                    ethers.utils.commify(ethers.utils.formatEther(whitelistMintCost)) + ` ${chainInformation.coinSymbol}`
                                ) : (
                                    <Skeleton width={50} />
                                )
                            }
                            info="The maximum allocation that can be reach."
                        />
                    </Stack>

                    <HeadingSection heading="Current Progress" icon={<IconActivity />} />

                    <Stack pl={0.5}>
                        <CustomListItem
                            heading="Fulfilled Allocation:"
                            text={`${
                                presaleTotalAllocations !== null ? ethers.utils.commify(presaleTotalAllocations) : <Skeleton width={50} />
                            } / ${presaleHardCap !== null ? ethers.utils.commify(presaleHardCap) + ' NFTs' : <Skeleton width={50} />}`}
                            info="Presale Progress"
                        />
                    </Stack>

                    <Stack>
                        <LinearProgress
                            sx={{ height: '15px', borderRadius: 2 }}
                            variant="determinate"
                            value={
                                presaleTotalAllocations !== null && presaleHardCap !== null
                                    ? (parseFloat(presaleTotalAllocations) / parseFloat(presaleHardCap)) * 100
                                    : 0
                            }
                        />
                    </Stack>

                    <HeadingSection heading="Mint NFTs" icon={<IconTicket />} />

                    <Stack pl={0.5}>
                        <CustomListItem
                            heading="Your Whitelist Status:"
                            text={
                                presaleUserWhitelistStatus ? (
                                    <Typography variant="p" color="#00ff00">
                                        Whitelisted
                                    </Typography>
                                ) : (
                                    <Typography variant="p">Not Whitelisted</Typography>
                                )
                            }
                            info="Whitelist entitles your to special access to this presale. This includes early access and reduced mint costs."
                        />
                        <CustomListItem
                            heading="Max Mint Per Transaction:"
                            text={'10'}
                            info="The amount of NFTs you can mint in a single transaction."
                        />
                    </Stack>

                    <TextField
                        id="presaleContribute"
                        label={`Mint Amount`}
                        type="number"
                        variant="filled"
                        sx={{ width: '100%' }}
                        inputProps={{ className: classes.input }}
                        value={presaleContributionValue}
                        placeholder="0"
                        defaultValue={1}
                        onChange={(e) => {
                            if (e.target.value.length !== undefined && e.target.value.length > 0) {
                                setPresaleContributionValue(Math.abs(e.target.value));
                            } else {
                                setPresaleContributionValue('');
                            }
                        }}
                        InputLabelProps={{
                            shrink: true
                        }}
                        helperText={
                            presaleContributionValue !== null &&
                            parseFloat(presaleContributionValue) > 0 &&
                            whitelistMintCost !== null &&
                            mintCost !== null
                                ? `Total Cost: ${ethers.utils.commify(
                                      parseInt(presaleContributionValue) *
                                          (presaleUserWhitelistStatus
                                              ? parseFloat(ethers.utils.formatEther(whitelistMintCost))
                                              : parseFloat(ethers.utils.formatEther(mintCost)))
                                  )} ${chainInformation.coinSymbol}`
                                : ''
                        }
                    />

                    <CustomButton
                        onClick={() => {
                            mint();
                        }}
                        variant="outlined"
                        startIcon={<IconTicket />}
                        disabled={parseInt(presaleTotalAllocations) == parseInt(presaleHardCap)}
                    >
                        {processing
                            ? 'Minting...'
                            : parseInt(presaleTotalAllocations) == parseInt(presaleHardCap)
                            ? 'Mint Completed'
                            : `Mint ${presaleContributionValue !== null ? presaleContributionValue : ''} NFT${
                                  presaleContributionValue > 0 ? 's' : ''
                              }`}
                    </CustomButton>
                    {selectedDropIndex !== -1 &&
                    databaseDropsData[selectedDropIndex].infoLink !== undefined &&
                    databaseDropsData[selectedDropIndex].infoLink.url.length > 0 &&
                    databaseDropsData[selectedDropIndex].infoLink.title.length > 0 ? (
                        <CustomButton
                            startIcon={<IconExternalLink />}
                            variant="outlined"
                            href={databaseDropsData[selectedDropIndex].infoLink.url}
                            target="_blank"
                            color="success"
                        >
                            {databaseDropsData[selectedDropIndex].infoLink.title}
                        </CustomButton>
                    ) : (
                        ''
                    )}
                </Stack>
            </Dialog>
            {chainInformation !== null ? (
                <Box sx={{ maxWidth: '1199px', margin: '0 auto' }}>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Grid container spacing={gridSpacing}>
                                <Grid item xs={12}>
                                    <Header
                                        background="url(resources/images/polygon-nft-background.jpg)"
                                        backgroundPosition="50% 50%"
                                        title="NFT Drops"
                                        subtitle="ICOs / Drops"
                                    />
                                    <Alert sx={{ marginTop: 3, borderRadius: 3 }} severity="info">
                                        Welcome to the Phenix Finance NFT Drops Page. Here, you will be able to access vetted Phenix Finance
                                        Official and Partner Token Presales / ICOs & NFT Drops on different blockchains.
                                    </Alert>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={4} direction="row">
                                        {databaseDropsData !== null
                                            ? databaseDropsData.map((_element, _index) => {
                                                  if (_element.adminMode == true && !isAdmin) return;
                                                  return (
                                                      <Grid key={_element.name} item xs={12} sm={6} md={4}>
                                                          <Stack key={_element.name} spacing={2}>
                                                              <Box sx={{ position: 'relative' }}>
                                                                  <Box
                                                                      sx={{
                                                                          width: '100%',
                                                                          paddingTop: '100%',
                                                                          borderRadius: '8px',
                                                                          backgroundColor: '#181a1d',
                                                                          position: 'relative'
                                                                      }}
                                                                  >
                                                                      <img
                                                                          style={{
                                                                              position: 'absolute',
                                                                              top: '50%',
                                                                              left: '50%',
                                                                              width: '75px',
                                                                              transform: 'translate(-50%, -50%)',
                                                                              opacity: 0.5
                                                                          }}
                                                                          src="resources/images/loader.svg"
                                                                          alt="loading"
                                                                      />
                                                                  </Box>
                                                                  <img
                                                                      src={_element.image}
                                                                      style={{
                                                                          width: '100%',
                                                                          border: '2px solid rgba(114,205,212, 0.2)',
                                                                          borderRadius: 8,
                                                                          boxShadow: 'rgba(114,205,212, 0.1) 0px 7px 29px 0px',
                                                                          position: 'absolute',
                                                                          top: 0,
                                                                          left: 0
                                                                      }}
                                                                      alt={_element.name}
                                                                  />
                                                                  <img
                                                                      src={`resources/images/drop_borders/${_element.chain}_border.png`}
                                                                      style={{
                                                                          width: '100%',
                                                                          border: '2px solid rgba(114,205,212, 0.2)',
                                                                          borderRadius: 8,
                                                                          boxShadow: 'rgba(114,205,212, 0.1) 0px 7px 29px 0px',
                                                                          position: 'absolute',
                                                                          top: 0,
                                                                          left: 0,
                                                                          zIndex: 2
                                                                      }}
                                                                      alt={_element.chain}
                                                                  />
                                                              </Box>

                                                              <Stack>
                                                                  <Stack alignItems="center" justifyContent="space-between" direction="row">
                                                                      <Typography
                                                                          variant="h4"
                                                                          color={
                                                                              _element.chain == 'cronos'
                                                                                  ? 'rgb(43, 175, 255)'
                                                                                  : 'rgb(200, 0, 255)'
                                                                          }
                                                                          style={{
                                                                              textShadow:
                                                                                  _element.chain == 'cronos'
                                                                                      ? '0px 0px 20px rgba(43, 175, 255, 0.75)'
                                                                                      : '0px 0px 20px rgba(200, 0, 255, 0.75)'
                                                                          }}
                                                                      >
                                                                          {_element.name}
                                                                      </Typography>
                                                                      <IconButton>
                                                                          <IconTicket color="white" />
                                                                      </IconButton>
                                                                  </Stack>

                                                                  <CustomListItem
                                                                      heading={
                                                                          _element.startTimestamp !== null &&
                                                                          _element.whitelistStartTimestamp !== null ? (
                                                                              _element.status == 0 ? (
                                                                                  'Starts In:'
                                                                              ) : (
                                                                                  'Status:'
                                                                              )
                                                                          ) : (
                                                                              <Skeleton width={50} />
                                                                          )
                                                                      }
                                                                      text={
                                                                          _element.startTimestamp !== null &&
                                                                          _element.whitelistStartTimestamp !== null ? (
                                                                              Math.floor(Date.now() / 1000) < _element.startTimestamp ? (
                                                                                  <Countdown date={_element.startTimestamp * 1000} />
                                                                              ) : (
                                                                                  <Typography
                                                                                      color={statusText(_element.status).color}
                                                                                      variant="p"
                                                                                  >
                                                                                      {_element.status !== undefined &&
                                                                                      _element.status !== null
                                                                                          ? statusText(_element.status).text
                                                                                          : 'In Progress'}
                                                                                  </Typography>
                                                                              )
                                                                          ) : (
                                                                              <Skeleton width={50} />
                                                                          )
                                                                      }
                                                                      info="Time until the presale is accessible."
                                                                  />

                                                                  <CustomListItem
                                                                      heading="Total Supply:"
                                                                      text={
                                                                          _element.totalSupply !== null ? (
                                                                              ethers.utils.commify(_element.totalSupply)
                                                                          ) : (
                                                                              <Skeleton width={50} />
                                                                          )
                                                                      }
                                                                      info="The maximum allocation that can be reach."
                                                                  />
                                                                  <CustomListItem
                                                                      heading="Mint Cost:"
                                                                      text={
                                                                          _element.mintCost !== null ? (
                                                                              ethers.utils.commify(_element.mintCost) +
                                                                              ` ${_element.coinSymbol}`
                                                                          ) : (
                                                                              <Skeleton width={50} />
                                                                          )
                                                                      }
                                                                      info="The maximum allocation that can be reach."
                                                                  />

                                                                  {_element.whitelistMintCost !== null ? (
                                                                      <CustomListItem
                                                                          heading="WL Cost:"
                                                                          text={
                                                                              _element.whitelistMintCost !== null ? (
                                                                                  ethers.utils.commify(_element.whitelistMintCost) +
                                                                                  ` ${_element.coinSymbol}`
                                                                              ) : (
                                                                                  <Skeleton width={50} />
                                                                              )
                                                                          }
                                                                          info="The maximum allocation that can be reach."
                                                                      />
                                                                  ) : (
                                                                      ''
                                                                  )}
                                                              </Stack>

                                                              <Button
                                                                  startIcon={<IconReportMoney />}
                                                                  onClick={() => {
                                                                      if (wallet == null) {
                                                                          setIsShowing(true);
                                                                      } else {
                                                                          handleViewDrop(_index);
                                                                      }
                                                                  }}
                                                                  variant="outlined"
                                                              >
                                                                  View Drop
                                                              </Button>
                                                          </Stack>
                                                      </Grid>
                                                  );
                                              })
                                            : ''}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                ''
            )}
        </>
    );
};

export default NFTDrops;
